import { useEffect, useState } from 'react';
import { http, formatError } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Check, Plus, X, TrendingUp, Users, Briefcase, ShieldCheck, ArrowUpRight } from 'lucide-react';

const money = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0);

function StatCard({ label, value, hint, icon: Icon, delay }) {
  return (
    <div className={`hq-card p-6 hq-fade-in-delay-${delay}`}>
      <div className="flex items-start justify-between mb-4">
        <span className="hq-eyebrow">{label}</span>
        {Icon && <Icon size={16} style={{ color: 'var(--gold)' }} strokeWidth={1.4} />}
      </div>
      <div className="font-serif text-4xl font-light" style={{ color: 'var(--sage)' }} data-testid={`stat-${label.toLowerCase().replace(/\s+/g,'-')}`}>
        {value}
      </div>
      {hint && (
        <div className="text-xs mt-2" style={{ color: 'var(--muted)', letterSpacing: '0.06em' }}>{hint}</div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [priorities, setPriorities] = useState([]);
  const [newTitle, setNewTitle] = useState('');
  const [adding, setAdding] = useState(false);

  const load = async () => {
    try {
      const [s, p] = await Promise.all([
        http.get('/dashboard/summary'),
        http.get('/priorities'),
      ]);
      setSummary(s.data);
      setPriorities(p.data);
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  useEffect(() => { load(); }, []);

  // Handle Stripe checkout return (?checkout=success&session_id=...)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const flag = params.get('checkout');
    const sessionId = params.get('session_id');
    if (flag !== 'success' || !sessionId) return;

    let cancelled = false;
    (async () => {
      try {
        const { data } = await http.get('/verify-checkout', { params: { session_id: sessionId } });
        if (cancelled) return;
        if (data?.status === 'paid') {
          const tierLabel = ({
            agent: 'Agent',
            agent_pro: 'Agent Pro',
            team: 'Team',
          })[data.tier] || 'HomiHQ';
          toast.success(`Welcome to ${tierLabel}. Your membership is live.`);
        } else {
          toast(`Checkout status: ${data?.status || 'pending'}`);
        }
      } catch (e) {
        toast.error(formatError(e));
      } finally {
        // Strip query params so refresh doesn't retrigger
        window.history.replaceState({}, '', window.location.pathname);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const addPriority = async (e) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    setAdding(true);
    try {
      const { data } = await http.post('/priorities', { title: newTitle, priority: 'medium' });
      setPriorities([data, ...priorities]);
      setNewTitle('');
    } catch (e) { toast.error(formatError(e)); }
    setAdding(false);
  };

  const toggle = async (p) => {
    try {
      const { data } = await http.patch(`/priorities/${p.id}`, { completed: !p.completed });
      setPriorities(priorities.map(x => x.id === p.id ? data : x));
    } catch (e) { toast.error(formatError(e)); }
  };

  const remove = async (p) => {
    try {
      await http.delete(`/priorities/${p.id}`);
      setPriorities(priorities.filter(x => x.id !== p.id));
    } catch (e) { toast.error(formatError(e)); }
  };

  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  const first = (user?.name || '').split(' ')[0];

  return (
    <div className="space-y-10">
      {/* Header */}
      <header className="hq-fade-in">
        <p className="hq-eyebrow">{today}</p>
        <h1 className="hq-h1 hq-page-title mt-3" data-testid="dashboard-title">Today</h1>
        <p className="mt-4 max-w-2xl" style={{ color: 'var(--ink-soft)', fontSize: '0.95rem', lineHeight: 1.7 }}>
          Good morning, {first}. Your AI employee has surveyed your business. Everything is prepared,
          nothing awaits without your consent.
        </p>
        <div className="hq-divider-gold w-fit"></div>
        <div
          className="mt-5 p-4 max-w-3xl text-xs"
          style={{ background: 'var(--parchment)', border: '1px solid var(--line)', borderRadius: 2, color: 'var(--ink-soft)', lineHeight: 1.6 }}
          data-testid="dashboard-privacy"
        >
          <div className="hq-eyebrow mb-2" style={{ color: 'var(--gold-soft)' }}>Your Data, Your Vault</div>
          Every client note, contract paste, and AI draft lives in your private HomiHQ workspace, encrypted at rest and
          accessible only to you and your team seats. Delete any record from its screen at any time. HomiHQ never sells,
          shares, or trains third-party models on your data.
        </div>
      </header>

      {/* Stats grid */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          label="Active Clients" delay={1}
          value={summary ? summary.active_clients : '—'}
          hint={summary ? `${summary.clients} total in pipeline` : ''}
          icon={Users}
        />
        <StatCard
          label="Qualified Leads" delay={1}
          value={summary ? summary.leads_qualified : '—'}
          hint={summary ? `${summary.leads} total inbound` : ''}
          icon={ShieldCheck}
        />
        <StatCard
          label="Deals In Motion" delay={2}
          value={summary ? summary.transactions_active : '—'}
          hint={summary ? `${summary.transactions_closed} closed YTD` : ''}
          icon={Briefcase}
        />
        <StatCard
          label="YTD GCI" delay={2}
          value={summary ? money(summary.ytd_gci) : '—'}
          hint={summary ? `${money(summary.pending_gci)} pending` : ''}
          icon={TrendingUp}
        />
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Priorities */}
        <div className="lg:col-span-2 hq-card p-8 hq-fade-in-delay-3">
          <div className="flex items-center justify-between mb-1">
            <div>
              <p className="hq-eyebrow">Your AI Employee's Morning Brief</p>
              <h2 className="hq-section-title mt-1">Priorities of the Day</h2>
            </div>
          </div>
          <div className="hq-divider-gold mb-6"></div>

          <form onSubmit={addPriority} className="flex gap-3 mb-6">
            <input
              className="hq-input flex-1"
              placeholder="Add a priority — e.g. Follow up with Ainsley on Beacon Hill offer"
              value={newTitle}
              onChange={e => setNewTitle(e.target.value)}
              data-testid="add-priority-input"
            />
            <button type="submit" disabled={adding} className="hq-btn-gold" data-testid="add-priority-btn">
              <Plus size={12} className="inline mr-1" /> Add
            </button>
          </form>

          <ul className="space-y-2" data-testid="priorities-list">
            {priorities.length === 0 && (
              <li className="py-8 text-center text-sm" style={{ color: 'var(--muted)' }}>
                No priorities yet. Add your first — or let your AI Employee draft them.
              </li>
            )}
            {priorities.map(p => (
              <li
                key={p.id}
                className="flex items-center gap-4 py-4 px-4 border-b"
                style={{ borderColor: 'var(--line-soft)' }}
                data-testid={`priority-item-${p.id}`}
              >
                <button
                  onClick={() => toggle(p)}
                  data-testid={`toggle-priority-${p.id}`}
                  className="w-5 h-5 border rounded-sm flex items-center justify-center transition"
                  style={{
                    borderColor: p.completed ? 'var(--gold)' : 'var(--line)',
                    background: p.completed ? 'var(--gold)' : 'transparent',
                  }}
                >
                  {p.completed && <Check size={12} color="#fff" strokeWidth={2.5} />}
                </button>
                <div className="flex-1">
                  <div
                    className={`text-sm ${p.completed ? 'line-through' : ''}`}
                    style={{ color: p.completed ? 'var(--muted)' : 'var(--ink)' }}
                  >
                    {p.title}
                  </div>
                </div>
                <span className="hq-eyebrow" style={{ color: 'var(--gold-soft)' }}>{p.priority}</span>
                <button onClick={() => remove(p)} data-testid={`delete-priority-${p.id}`}>
                  <X size={14} style={{ color: 'var(--muted)' }} />
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Market Pulse */}
        <aside className="hq-card p-8 hq-fade-in-delay-3">
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="hq-eyebrow">Market Pulse</p>
              <h3 className="hq-section-title text-2xl mt-1">Quiet Signals</h3>
            </div>
            <span
              className="hq-eyebrow shrink-0 px-2 py-1"
              style={{ background: 'var(--parchment)', border: '1px solid var(--gold)', color: 'var(--gold-soft)', borderRadius: 2 }}
              data-testid="market-pulse-demo-tag"
            >
              Sample
            </span>
          </div>
          <div className="hq-divider-gold mb-6"></div>

          <p className="text-xs mb-5" style={{ color: 'var(--muted)', lineHeight: 1.6 }}>
            Demo signals shown below. Live, hyper-local market data is generated on demand in{' '}
            <a href="/intel" style={{ color: 'var(--gold-soft)', textDecoration: 'underline' }}>Neighborhood Intel</a>.
          </p>

          <div className="space-y-5 text-sm" style={{ color: 'var(--ink-soft)', lineHeight: 1.65 }}>
            <div>
              <div className="hq-eyebrow mb-1">Inventory</div>
              <div>National luxury inventory has been tightening in premier zip codes through the last quarter (sample view).</div>
            </div>
            <div style={{ borderTop: '1px solid var(--line-soft)', paddingTop: '1rem' }}>
              <div className="hq-eyebrow mb-1">Buyer Sentiment</div>
              <div>Cash buyers remain active despite rate volatility — timing favors decisive sellers (sample view).</div>
            </div>
            <div style={{ borderTop: '1px solid var(--line-soft)', paddingTop: '1rem' }}>
              <div className="hq-eyebrow mb-1">Your Volume YTD</div>
              <div className="font-serif text-3xl" style={{ color: 'var(--sage)' }} data-testid="volume-ytd">
                {summary ? money(summary.ytd_volume) : '—'}
              </div>
            </div>
          </div>

          <a href="/intel" className="hq-btn-ghost mt-6 inline-flex items-center gap-2" data-testid="open-intel-link">
            Neighborhood Intel <ArrowUpRight size={12} />
          </a>
        </aside>
      </div>
    </div>
  );
}
